"use client";

import React from "react";
import { ArrowRight, Phone, ShieldCheck, CircleCheck as CheckCircle2, Building2, House as Home, Hammer, Construction } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Link } from "@/components/common/Link";
const accessTypes = [{
  icon: Building2,
  title: "Commercial",
  desc: "Independent access, system scaffolds, and site-compliant solutions."
}, {
  icon: Home,
  title: "Residential",
  desc: "Extensions, repointing, decorations, and homeowner-friendly access."
}, {
  icon: Hammer,
  title: "Roofing access",
  desc: "Chimney scaffolds, solar panel mounting, and roof repair platforms."
}, {
  icon: Construction,
  title: "Specialist works",
  desc: "Temporary roofs, cantilevered towers, and complex custom designs."
}];
const accreditations = ["NASC Member", "CHAS Accredited"];
export default function Fleet() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="fleethero" data-nav="dark" className="relative overflow-hidden bg-secondary text-secondary-foreground py-16 sm:py-20 md:py-28">
      {/* Subtle architectural grid pattern background */}
      <div className="absolute inset-0 pointer-events-none opacity-5 bg-[linear-gradient(to_right,currentColor_1px,transparent_1px),linear-gradient(to_bottom,currentColor_1px,transparent_1px)] bg-[size:4rem_4rem]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* Main Hero Copy Column */}
          <div className="lg:col-span-7 flex flex-col items-start space-y-6">
            <motion.div variants={itemVariants} className="inline-flex items-center gap-2">
              <Badge variant="outline" className="bg-secondary-foreground/10 text-secondary-foreground border-secondary-foreground/20 px-3 py-1 text-xs sm:text-sm font-medium rounded-full">
                <ShieldCheck className="w-4 h-4 text-primary shrink-0 mr-1" />
                Fleet & Hampshire Scaffolding Contractors
              </Badge>
            </motion.div>

            <motion.h1 variants={itemVariants} className="font-default text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-secondary-foreground leading-tight">
              Scaffolding in Fleet
            </motion.h1>

            <motion.p variants={itemVariants} className="text-base sm:text-lg md:text-xl text-secondary-foreground/80 max-w-2xl leading-relaxed">
              Safety-led scaffolding for commercial projects, residential properties, roofing works, and specialist access structures across Fleet and the surrounding Home Counties.
            </motion.p>

            {/* CTAs */}
            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto pt-2">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 shadow-md font-medium text-base h-12 px-7 transition-[background-color,box-shadow]">
                <Link to="/ContactRequestQuotePage" className="inline-flex items-center justify-center gap-2">
                  <span>Request a quote</span>
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </Button>

              <Button asChild variant="outline" size="lg" className="border-secondary-foreground/30 bg-secondary/80 text-secondary-foreground hover:bg-secondary-foreground/10 hover:text-secondary-foreground font-medium text-base h-12 px-6 transition-[background-color,border-color]">
                <a href="tel:01344929900" className="inline-flex items-center justify-center gap-2 text-secondary-foreground hover:text-secondary-foreground">
                  <Phone className="w-4 h-4 text-primary" />
                  <span>Call 01344 929900</span>
                </a>
              </Button>
            </motion.div>

            {/* Trust Markers / Accreditations */}
            <motion.div variants={itemVariants} className="pt-6 border-t border-secondary-foreground/15 w-full flex flex-wrap items-center gap-y-3 gap-x-6 text-xs sm:text-sm text-secondary-foreground/75">
              <span className="font-semibold text-secondary-foreground">Verified standards:</span>
              {accreditations.map((badgeText, idx) => <div key={badgeText} data-index={idx} className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-4 h-4 text-primary shrink-0" />
                  <span>{badgeText}</span>
                </div>)}
            </motion.div>
          </div>

          {/* Service Capabilities Cards Column */}
          <div className="lg:col-span-5">
            <motion.div variants={itemVariants} className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 p-4 sm:p-6 rounded-2xl bg-secondary-foreground/5 border border-secondary-foreground/10 backdrop-blur-xs">
              {accessTypes.map((service, index) => {
              const Icon = service.icon;
              return <div key={service.title} data-index={index} className="p-4 rounded-xl bg-secondary/80 border border-secondary-foreground/10 shadow-xs flex flex-col justify-between space-y-3 hover:border-primary/50 transition-[border-color,background-color] duration-200">
                    <div className="w-10 h-10 rounded-lg bg-primary/15 text-primary flex items-center justify-center shrink-0">
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h2 className="font-default text-base font-semibold text-secondary-foreground leading-snug">
                        {service.title}
                      </h2>
                      <p className="text-xs sm:text-sm text-secondary-foreground/70 mt-1 leading-relaxed">
                        {service.desc}
                      </p>
                    </div>
                  </div>;
            })}
            </motion.div>
          </div>
        </motion.div>
      </div>
    </section>;
}